/**
 * Generic file reading utilities for CSV and XLSX files
 */

import ExcelJS from 'exceljs';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB

/**
 * Validates file size
 * @param {File} file - The file to validate
 * @throws {Error} If file exceeds size limit
 */
function validateFileSize(file) {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error('O arquivo é muito grande. O limite é 2MB.');
  }
}

/**
 * Reads a CSV file and returns parsed data
 * @param {File} file - The CSV file to read
 * @returns {Object} Object with headers and rawData arrays
 */
export async function readCSV(file) {
  validateFileSize(file);
  
  const text = await file.text();
  const lines = text.split('\n');
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  
  const rawData = lines.slice(1)
    .filter(line => line.trim())
    .map((line, index) => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const item = {};
      headers.forEach((header, headerIndex) => { 
        item[header] = values[headerIndex]; 
      });
      item.lineNumber = index + 2; // Linha 1 são os headers
      return item;
    });

  return { headers, rawData };
}

/**
 * Reads an XLSX file and returns parsed data
 * @param {File} file - The XLSX file to read
 * @returns {Object} Object with headers and rawData arrays
 */
export async function readXLSX(file) {
  validateFileSize(file);
  
  try {
    
    const dataBuffer = await file.arrayBuffer();
    
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(dataBuffer);
    console.log('✅ Workbook carregado, worksheets:', workbook.worksheets.length);
    
    const worksheet = workbook.getWorksheet(1);
    if (!worksheet) {
      throw new Error('O arquivo XLSX está vazio ou não possui planilhas.');
    }
    
    // Listar todas as worksheets e tentar cada uma
    let workingWorksheet = null;
    let testData = [];
    
    for (let wsIndex = 1; wsIndex <= workbook.worksheets.length; wsIndex++) {
      const testWs = workbook.getWorksheet(wsIndex);
      
      // Testar diferentes métodos de acesso
      let foundDataInWs = false;
      
      // Expandir para ler mais linhas - testar até encontrar uma linha vazia
      for (let row = 1; row <= 100; row++) { // Aumentado para 100 linhas
        const rowData = [];
        let hasData = false;
        
        for (let col = 1; col <= 20; col++) { // Aumentado para 20 colunas
          try {
            const cell = testWs.getCell(row, col);
            // Remover log detalhado para não poluir o console
            const value = cell.value;
            rowData.push(value);
            if (value !== null && value !== undefined && value !== '') {
              hasData = true;
              foundDataInWs = true;
            }
          } catch (e) {
            rowData.push(null);
          }
        }
        
        if (hasData) {
          testData.push({ row, data: rowData });
        } else if (row > 1 && testData.length > 0) {
          // Se não há dados nesta linha e já encontramos dados antes, provavelmente acabaram os dados
          break;
        }
      }
      
      if (foundDataInWs) {
        console.log(`✅ Dados encontrados na worksheet "${testWs.name}"`);
        workingWorksheet = testWs;
        break;
      } else {
        console.log(`⚠️ Nenhum dado na worksheet "${testWs.name}"`);
        
        // Se não encontrou dados com o método 1, pular método 2 para essa worksheet
        console.log(`  ⚠️ Método 1 não encontrou dados na worksheet "${testWs.name}", tentando próxima...`);
      }
    }
    
    // Se ainda não encontrou dados, tentar abordagem mais agressiva
    if (!workingWorksheet) {
      console.log('🚨 Tentativa final: métodos alternativos do ExcelJS');
      const ws = workbook.getWorksheet(1);
      
      // Tentar usar spliceRows
      try {
        const rows = ws.spliceRows(1, 5);
      } catch (e) {
        console.log('❌ spliceRows falhou:', e.message);
      }
      
      // Tentar usar getColumn
      try {
        const colA = ws.getColumn(1);
        const colB = ws.getColumn(2);
        
        if (colA.values && colA.values.length > 1) {
          // Reconstruir dados a partir das colunas
          testData = [];
          const maxRows = Math.max(colA.values.length, colB.values.length);
          
          for (let i = 1; i < maxRows; i++) {
            const rowData = [];
            let hasData = false;
            
            for (let colNum = 1; colNum <= 10; colNum++) {
              try {
                const col = ws.getColumn(colNum);
                const value = col.values[i];
                rowData.push(value);
                if (value !== null && value !== undefined && value !== '') {
                  hasData = true;
                }
              } catch (e) {
                rowData.push(null);
              }
            }
            
            if (hasData) {
              testData.push({ row: i, data: rowData });
            }
          }
          
          workingWorksheet = ws;
        }
      } catch (e) {
        console.log('❌ getColumn falhou:', e.message);
      }
    }
    
    console.log('📊 Dados encontrados:', testData.length, 'linhas');
    
    if (testData.length === 0) {
      throw new Error('Nenhum dado foi encontrado no arquivo XLSX. Pode ser um problema de compatibilidade.');
    }
    
    // Extrair headers da primeira linha com dados
    const firstDataRow = testData[0];
    const headers = [];
    
    for (let i = 0; i < firstDataRow.data.length; i++) {
      const value = firstDataRow.data[i];
      if (value !== null && value !== undefined && value !== '') {
        headers.push(String(value).trim());
      } else {
        break; // Para na primeira célula vazia
      }
    }
    
    console.log('📋 Headers extraídos:', headers);
    
    if (headers.length === 0) {
      throw new Error('Não foi possível extrair cabeçalhos válidos.');
    }
    
    // Processar dados das linhas restantes
    const json = [];
    
    for (let i = 1; i < testData.length; i++) {
      const row = testData[i];
      const rowData = {};
      let hasRowData = false;
      
      for (let j = 0; j < headers.length; j++) {
        const header = headers[j];
        const value = row.data[j];
        const stringValue = value !== null && value !== undefined ? String(value).trim() : '';
        
        rowData[header] = stringValue;
        if (stringValue !== '') hasRowData = true;
      }
      
      if (hasRowData) {
        rowData.lineNumber = row.row;
        json.push(rowData);
      }
    }
    
    console.log('📊 Resultado:', { headers: headers.length, dados: json.length });
    
    if (json.length === 0) {
      throw new Error('Não foi possível extrair dados válidos após os cabeçalhos.');
    }
    
    return { headers, rawData: json };
    
  } catch (error) {
    console.error('Erro ao ler arquivo XLSX:', error);
    throw new Error(`Erro ao processar arquivo XLSX: ${error.message}`);
  }
}

/**
 * Generic function to read file based on type
 * @param {File} file - The file to read
 * @returns {Object} Object with headers and rawData arrays
 */
export async function readFile(file) {
  const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
  const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
  
  if (isCSV) {
    return readCSV(file);
  } else if (isXLSX) {
    return readXLSX(file);
  } else {
    throw new Error('Formato de arquivo não suportado. Envie um arquivo CSV ou XLSX.');
  }
}
